import { FC, createContext, useMemo, useEffect, useState } from 'react';

import { ApiError, InputError, MessageError } from 'components/ErrorBoundary';
import { GlobalProviderProps, CbFuncType } from 'providers/Global/types';
import { AppConfigStateType } from 'hooks/appConfig/types';
import { TRoute } from 'routes/types';
import { QuickReplyPayload } from 'components/MessageExtensions/QuickReplies/types';

import { putPropelMojoContext } from 'services/propel';
import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { formatMessages, secureUserMessage } from 'utils/message';
import { pollUntilConditionMet } from 'utils/poll';
import { getMojoContext, mapMojoContextNativeMobileApp } from 'utils/chatContext';
import { showNameMsgAndEscalate } from './methods/showNameMsgAndEscalate';
import { showAiMessage } from './methods/showAiMessage';

import { MojoContextType, NativeAppPutContextType } from 'utils/chatContext/types';
import { ANALYTICS } from 'analytics/constants';
import { ROUTES } from 'routes/constants';
import { MILLISECONDS_PER_MINUTE } from 'constants/date';
import { CHATUI_WINDOW_PATH, CHATUI_WINDOW_PATH_OLD } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import {
  CONVERSATION_MESSAGES_MAX,
  INACTIVITY_TIMEOUT_MINUTES,
  TWILIO_CONNECTION_STATE,
} from 'constants/twilio';
import { AnalyticInactivityShownDetailsType } from 'analytics/types';
import { startConversation } from 'services/chatBackend';
import { PropelAuthType } from 'services/propel/types';

export const GlobalContext = createContext<any>({});

let inactivityCounter: any = null;

const GlobalProvider: FC<PropsWithChildren<GlobalProviderProps>> = ({
  children,
  location,
  navigate,
  storeFunctions,
  storeStates,
}) => {
  // callback function definitions for external applications
  const [onHideCallbacks, setOnHideCallbacks] = useState<CbFuncType>([]);
  const [onShowCallbacks, setOnShowCallbacks] = useState<CbFuncType>([]);

  const {
    dispatchAppConfigAction,
    dispatchAuthenticationAction,
    dispatchChatContextAction,
    dispatchConversationAction,
    dispatchCustomerAction,
    dispatchViewAction,
    trackEvent,
  } = storeFunctions;
  const { appConfigState, authenticationState, chatContextState, conversationState, viewState } =
    storeStates;

  useEffect(() => {
    if (appConfigState.isNativeMobileApp) {
      window?.webkit?.messageHandlers?.chatApiReady.postMessage('');
    }
  }, []);

  useEffect(() => {
    // special hook needed for inactivity due to interval. Register callbacks.
    if (conversationState.isInactivityTimeout) {
      trackEvent({
        analyticEvent: ANALYTICS.EVENT.INACTIVITY_SHOWN,
        details: { is_chat_opened: viewState.isPanelOpen } as AnalyticInactivityShownDetailsType,
      });
      if (!viewState.isPanelOpen) {
        if (appConfigState.isNativeMobileApp) {
          window?.webkit?.messageHandlers?.errorNotificationBadgeChanged.postMessage('');
        }
      }
    }
  }, [conversationState.isInactivityTimeout]);

  /** START ChatUI getter API methods **/
  const getAdvocates = () => conversationState.participants.advocates;
  const getActivePanel = () => location.pathname;
  const getConversationId = () => authenticationState.propel.conversationId || null;
  const getChatContext = () => chatContextState.mojoContext;
  const getConnectionState = () => conversationState.twilio?.client?.connectionState || undefined;
  const getViewState = () => viewState;
  const getIsConnected = () => conversationState.isConnected;
  const getParticipants = () => conversationState.participants || [];
  const getReferrer = () => appConfigState.referrer || undefined;
  const getStoreStates = () => console.log(storeStates);
  const getTwilioConversationSid = () => conversationState.twilio?.conversation?.sid ?? '';

  const getMessages = async () => {
    // manually fetch latest messages from Twilio. Previous message history may have been updated via external API but
    // not be reflected in conversationState (such as form inputs). ConversationState hook only listens for new messages.
    const messages = await conversationState.twilio?.conversation.getMessages(
      CONVERSATION_MESSAGES_MAX,
    );
    return messages ? formatMessages(messages.items) : [];
  };
  /** END ChatUI getter API methods **/

  /** Initialize Chat and Render it on the page */
  const init = () => {
    changeConfig({ ...appConfigState, isEnabled: true });
  };

  const changeConfig = (config: AppConfigStateType) => {
    dispatchAppConfigAction({
      type: ACTIONS.CHANGE,
      payload: { ...appConfigState, config },
    });
  };

  const changeRoute = (route: TRoute) => {
    navigate(route);
  };

  const putContext = async (context: MojoContextType | NativeAppPutContextType) => {
    let newMojoContext = context;
    if (appConfigState.isNativeMobileApp) {
      // @ts-ignore // eslint-disable-next-line
      newMojoContext = mapMojoContextNativeMobileApp(context);

      // FIXME - temporary scaffold during iOS renaming process
      const isOldName = Boolean(window?.webkit?.messageHandlers?.putContextPromise);
      const isNewName = Boolean(window?.webkit?.messageHandlers?.onPutContext);
      if (isOldName) {
        window?.webkit?.messageHandlers?.putContextPromise.postMessage('');
      } else if (isNewName) {
        window?.webkit?.messageHandlers?.onPutContext.postMessage('');
      }
    }
    /**
     * If conversation already exists, API can be called on-demand to update context in real-time.
     * If no conversation, consumer that is programmatically accessing ChatUI (like iOS app) should invoke putContext
     * before opening chat. No API call will be made, but data store will have values ready on initialization.
     */
    if (getConversationId()) {
      await putPropelMojoContext(newMojoContext, authenticationState.propel);
    }
    // Align chatContext data store. Remove handraiser info if present so future putContext calls will reflect URL navigation without handraiser.
    // FIXME - mobile native context needs to be revised to align with Web
    dispatchChatContextAction({
      type: ACTIONS.CHANGE,
      payload: {
        mojoContext: {
          ...newMojoContext,
          info: { ...newMojoContext.info, handraiser: null },
        },
        urlCurrentAiContext: window.location.href,
        urlPreviousAiContext: chatContextState.urlCurrentAiContext,
      },
    });
  };

  const sendMessage = async (
    message: string | any,
    enginePayload?: QuickReplyPayload,
    updateContext = true,
  ) => {
    if (typeof message !== 'string' || !message.length) {
      // fail-safe since typing can't be enforced during global invocation. Provide context in console.
      const err = new InputError(
        `New message must be non-empty AND string data type.\nReceived: ${JSON.stringify(
          message,
        )}\nType: ${typeof message}`,
      );
      console.log(err);
      throw err;
    }
    if (!conversationState.twilio) {
      const err = new ApiError(
        `Twilio client and/or conversation is not initialized. Cannot send message: "${message}"`,
      );
      console.log(err);
      throw err;
    }
    // At this point, Twilio client & conversation exist in data stores.
    // But websocket connection may have closed due to inactivity & reconnect needed.
    const safeMsg = secureUserMessage(message);

    // Set new AI context if URL has changed but no page routing has actually taken place.
    // Especially notable for some Single-Page-Applications (SPAs) like Purchase & STC.
    const urlCurrent = window.location.href;
    const hasUrlChanged = urlCurrent !== chatContextState.urlCurrentAiContext;
    if (updateContext && hasUrlChanged) {
      const newContext = getMojoContext();
      // @ts-ignore //eslint-disable-next-line
      await putContext(newContext);
      const urls = {
        urlCurrentAiContext: urlCurrent,
        urlPreviousAiContext: chatContextState.urlCurrentAiContext,
      };
      DataDogLogger.action(CUSTOM_ACTION_NAMES.CONTEXT_URL_UPDATED_NO_RELOAD, urls);
    }

    const clientConnectionState = conversationState.twilio?.client?.connectionState;
    if (clientConnectionState === TWILIO_CONNECTION_STATE.CONNECTED) {
      try {
        // NOTE: special `payload` property will not appear as text in Front conversations or have any future tracing. Pay close attention.
        const msgBody = { text: safeMsg, ...(enginePayload && { payload: enginePayload }) };
        await conversationState.twilio.conversation.sendMessage(JSON.stringify(msgBody));
        DataDogLogger.action(CUSTOM_ACTION_NAMES.USER_MESSAGE_SENT, {
          clientConnectionState,
          invocation: 'global API',
        });
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: {
            isExistingConversation: true,
            isInactivityTimeout: false,
            questionWhenInitializing: '',
            questionInputText: '',
          },
        });
      } catch (e: any) {
        throw new MessageError('Issue sending new message', {
          invocation: 'global API',
          cause: e,
        });
      }
    } else {
      // Customer.tsx will handle async Twilio reconnect via isInitializationRequested & initializeConversation().
      // QuestionField.tsx will circular route back here to sendMessage() after reconnect via isNewMsgWaitingForTwilioReconnect & conversationState.isConnected.
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: {
          isConnected: false,
          isInitializationRequested: true,
          questionWhenInitializing: safeMsg,
          questionInputText: safeMsg,
        },
      });
    }
  };

  const setReferrer = (origin: string) => {
    dispatchAppConfigAction({
      type: ACTIONS.CHANGE,
      payload: { ...appConfigState, referrer: origin || undefined },
    });
  };

  /**
   * Creates a conversation.
   * @returns a Promise that resolves with the conversation information
   */
  const startConversationAsync = async (forceNewAuthTokens = false): Promise<PropelAuthType> => {
    const existingConversationId = authenticationState?.propel?.conversationId;
    if (existingConversationId) {
      DataDogLogger.action('Chat UI API - createConversationAsync: Existing conversationId found.');
    }
    const result = await startConversation(forceNewAuthTokens);
    // Update store states with new conversation information
    dispatchAuthenticationAction({
      type: ACTIONS.CHANGE,
      payload: {
        propel: result,
      },
    });
    return result;
  };

  const startInactivityCounter = () => {
    // use date to preserve counter even through computer sleeping/shutdown
    const initialDate = new Date().valueOf();
    const ONE_MINUTE = MILLISECONDS_PER_MINUTE;
    clearInterval(inactivityCounter);
    inactivityCounter = setInterval(() => {
      const minutesDiff = Math.round(Math.abs((initialDate - new Date().valueOf()) / ONE_MINUTE));
      if (appConfigState.isDebugMode) {
        console.log('ChatUI debug - minutes since last activity:', minutesDiff);
      }
      if (minutesDiff >= INACTIVITY_TIMEOUT_MINUTES) {
        if (appConfigState.isDebugMode) {
          console.log(
            'ChatUI debug - inactivity timeout minutes reached:',
            INACTIVITY_TIMEOUT_MINUTES,
          );
        }
        clearInterval(inactivityCounter);
        const nextState = {
          ...conversationState,
          isConnected: false,
          isInactivityTimeout: true,
          isInitializationRequested: false,
        };
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: nextState,
        });
      }
    }, ONE_MINUTE);
  };

  /**
   * Initialize a conversation and open the Chat.
   *
   * This method dispatches a conversation action to trigger initialization of
   * a conversation.
   * The useEffect hook in Chat.tsx will trigger, a conversation will be started, put context will be sent, and the twilio connection will be established.
   * @returns
   */
  const initializeConversation = async () => {
    if (conversationState.isConnected) {
      console.log('ERROR!! Conversation is already initialized');
      return true;
    }
    try {
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: { isInitializationRequested: true },
      });
      navigate(ROUTES.CHAT);
      show();
      await pollUntilConditionMet({
        conditionFn: () => Boolean(window[CHATUI_WINDOW_PATH].getTwilioConversationSid()),
        description: 'polling for existence of Twilio conversation',
      });
      return true;
    } catch (e: any) {
      const err = new ApiError(
        'Unable to programmatically initialize conversation via initializeConversation',
      );
      console.log(e);
      throw err;
    }
  };

  const show = () => {
    if (appConfigState.isNativeMobileApp) {
      window?.webkit?.messageHandlers?.onShow.postMessage('');
    }
    for (const cb of onShowCallbacks) {
      if (typeof cb === 'function') cb();
    }
    dispatchViewAction({
      type: ACTIONS.CHANGE,
      payload: {
        ...viewState,
        isPanelOpen: true,
        errorBoundary: {
          ...viewState.errorBoundary,
          isVisible: false,
        },
      },
    });
  };

  const hide = () => {
    if (appConfigState.isNativeMobileApp) {
      window?.webkit?.messageHandlers?.onHide.postMessage('');
    }
    for (const cb of onHideCallbacks) {
      if (typeof cb === 'function') cb();
    }
    dispatchViewAction({
      type: ACTIONS.CHANGE,
      payload: {
        ...viewState,
        isPanelOpen: false,
        errorBoundary: {
          ...viewState.errorBoundary,
          isVisible: false,
        },
      },
    });
  };

  const value = useMemo(
    () => ({
      show,
      hide,
    }),
    [],
  );

  const api = {
    init,
    initializeConversation,
    changeConfig,
    changeRoute,
    putContext,
    getActivePanel,
    getAdvocates,
    getChatContext,
    getConnectionState,
    getViewState,
    getConversationId,
    getIsConnected,
    getMessages,
    getParticipants,
    getReferrer,
    getStoreStates,
    getTwilioConversationSid,
    show,
    hide,
    setReferrer,
    sendMessage,
    startConversationAsync,
    startInactivityCounter,
    showNameMsgAndEscalate: () => showNameMsgAndEscalate(authenticationState, conversationState),
    showAiMessage: () => showAiMessage(authenticationState, conversationState),
    getUnreadCount: () => conversationState.messagesUnreadCount,
    toggleChat: () => {
      viewState.isPanelOpen ? hide() : show();
    },
    // mostly ported logic from ChatAI. Particular requirements for iOS app.
    shouldShowErrorNotificationBadge: () => {
      const isChatPanel = getActivePanel() === ROUTES.CHAT;
      const isNotInitializing = !conversationState.isInitializationRequested;
      const isConnectionDenied = getConnectionState() === TWILIO_CONNECTION_STATE.DENIED;
      return (
        isChatPanel &&
        !isNotInitializing &&
        (isConnectionDenied || conversationState.isInactivityTimeout)
      );
    },
    onHide: (cb: () => void) => {
      setOnHideCallbacks([...onHideCallbacks, cb]);
    },
    onShow: (cb: () => void) => {
      setOnShowCallbacks([...onShowCallbacks, cb]);
    },
    onHideCallbacks,
    onShowCallbacks,
  };

  window[CHATUI_WINDOW_PATH] = api;
  window[CHATUI_WINDOW_PATH_OLD] = api;

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export default GlobalProvider;
